import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { space as styledSpace, SpaceProps } from 'styled-system';

interface MenuButtonStyledProps extends SpaceProps {
  $size: number;
  $primary: boolean;
  $active: boolean;
  $disabled: boolean;
}

const MenuButtonStyled = styled.button<MenuButtonStyledProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  width: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-height: ${({ $size }) => $size}px;
  color: ${({ theme, $primary }) =>
    $primary ? theme.colors.primary.main : theme.colors.text.secondary};
  transition: 0.2s background-color ease-in-out;
  cursor: pointer;
  border: none;
  background: transparent;

  & svg {
    color: inherit;
  }

  &:hover:not(:disabled) {
    color: ${({ theme, $primary }) =>
      $primary ? theme.colors.primary.main : theme.colors.text.primary};
    background: ${({ theme, $primary }) =>
      $primary ? theme.colors.highlight.blue25 : theme.colors.highlight.gray25};
  }

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      color: ${theme.colors.text.disabled};
      cursor: default;
      pointer-events: none;

      &:hover {
        background: none;
      }

      & svg {
        color: ${theme.colors.text.disabled};
      }
    `}

  ${({ $active, theme }) =>
    $active &&
    css`
      color: ${theme.colors.primary.main};

      & svg {
        color: ${theme.colors.primary.main};
      }

      &:hover:not(:disabled) {
        color: ${theme.colors.primary.dark};
        background: ${theme.colors.highlight.blue25};

        & svg {
          color: ${theme.colors.primary.dark};
        }
      }
    `}

  ${styledSpace}
`;

export interface MenuButtonProps extends SpaceProps {
  type?: 'button' | 'submit' | 'reset';
  size?: number;
  primary?: boolean;
  active?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  /** Состояние раскрытия для кнопки-триггера выпадающего меню. */
  'aria-expanded'?: boolean;
  children: ReactNode;
  onClick?: () => void;
}

export function MenuButton({
  type = 'button',
  size = 34,
  primary = false,
  active = false,
  disabled = false,
  children,
  onClick,
  ...spaceProps
}: MenuButtonProps) {
  return (
    <MenuButtonStyled
      type={type}
      $size={size}
      $primary={primary}
      $active={active}
      $disabled={disabled}
      disabled={disabled}
      onClick={onClick}
      {...spaceProps}
    >
      {children}
    </MenuButtonStyled>
  );
}
